import React from 'react';
import { Link } from 'react-router';

import type { FavoriteCampaignInfoV1 } from '~/api/v1/mall/favorite-campaign';
import type { SurfaceSlotV1 } from '~/api/v1/mall/surface/surface-name';
import type { RecommendShopItemV2 } from '~/api/v2/mall/recommend-shop';
import type { CampaignInfoV2 } from '~/api/v2/schemas/campaign-info';
import usePreventDoubleClick from '~/hooks/use-prevent-double-click';
import { CampaignLogo, type CampaignLogoData, type CampaignLogoStyles } from '~/ui/campaign-logo';
import { Favorite } from '~/ui/favorite';

export type CampaignCardStyles = {
  box: string;
  layout: string;
  name: string;
  logo: CampaignLogoStyles;
  pointSlot: string;
  favoriteSlot?: string;
  rankSlot?: string;
};

type PropsCommon = {
  styles: CampaignCardStyles;
  favoriteContent?: {
    renderFavorite: ({ isFavorite }: { isFavorite: boolean }) => React.ReactNode;
    isLoggedIn: boolean;
    redirectToLogin: string;
  };
  renderPoint: () => React.ReactNode;
  externalLink?: (path: string) => string;
  children?: React.ReactNode;
  rankContent?: React.ReactNode;
  lazy?: boolean;
};

export type CampaignCardProps =
  | (PropsCommon & { campaignInfo: CampaignInfoV2 })
  | (PropsCommon & { recommendShopDetail: RecommendShopItemV2 })
  | (PropsCommon & { favoriteCampaign: FavoriteCampaignInfoV1 })
  | (PropsCommon & { surfaceSlot: SurfaceSlotV1 });

const renderFavorite = (props: CampaignCardProps, className?: string) => {
  if (!props.favoriteContent) return null;

  if ('campaignInfo' in props) {
    return (
      <Favorite className={className} campaignInfo={props.campaignInfo} isLoggedIn={true} redirectToLogin="/login">
        {props.favoriteContent.renderFavorite}
      </Favorite>
    );
  } else if ('recommendShopDetail' in props) {
    return (
      <Favorite
        className={className}
        recommendShopDetail={props.recommendShopDetail}
        isLoggedIn={props.favoriteContent.isLoggedIn}
        redirectToLogin={props.favoriteContent.redirectToLogin}>
        {props.favoriteContent.renderFavorite}
      </Favorite>
    );
  } else if ('favoriteCampaign' in props) {
    return (
      <Favorite
        className={className}
        favoriteCampaign={props.favoriteCampaign}
        isLoggedIn={props.favoriteContent.isLoggedIn}
        redirectToLogin={props.favoriteContent.redirectToLogin}>
        {props.favoriteContent.renderFavorite}
      </Favorite>
    );
  }

  return null;
};

export const CampaignCard = (props: CampaignCardProps) => {
  const { styles, children, lazy = true } = props;
  const preventDoubleClick = usePreventDoubleClick({ delay: 2000 });

  let data: CampaignInfoV2 | RecommendShopItemV2 | FavoriteCampaignInfoV1 | SurfaceSlotV1;
  let campaignType: string;
  let logoData: CampaignLogoData;

  if ('campaignInfo' in props) {
    data = props.campaignInfo;
    campaignType = data.campaign_type === 1 ? 'shop' : 'offer';
    logoData = data;
  } else if ('recommendShopDetail' in props) {
    data = props.recommendShopDetail;
    campaignType = 'shop';
    logoData = data;
  } else if ('favoriteCampaign' in props) {
    data = props.favoriteCampaign;
    campaignType = 'shop';
    logoData = data;
  } else if ('surfaceSlot' in props) {
    // surface_nameが'recommend_shop'の時にはcampaignとして運用する
    const slot = props.surfaceSlot;
    campaignType = 'shop';
    logoData = {
      campaign_id: slot.campaign_id ?? -1,
      campaign_name: slot.campaign_name ?? '',
      logo_path: slot.image1_path ?? '',
    };
    data = slot;
  } else {
    throw new Error('unreachable');
  }

  const content = (
    <>
      <CampaignLogo data={logoData} styles={styles.logo} lazy={lazy} />
      <p className={styles.name}>{data.campaign_name}</p>
      <div className={styles.pointSlot}>{props.renderPoint()}</div>
      {renderFavorite(props, styles.favoriteSlot)}
      {props.rankContent && styles.rankSlot && <div className={styles.rankSlot}>{props.rankContent}</div>}
      {children}
    </>
  );

  const path = `/${campaignType}/${data.campaign_id}`;

  if (props.externalLink) {
    // external link
    const href = props.externalLink(path);
    return (
      <div className={styles.box}>
        <a
          href={href}
          className={styles.layout}
          target="_blank"
          rel="noreferrer noopener"
          onClick={preventDoubleClick()}>
          {content}
        </a>
      </div>
    );
  } else {
    // internal link
    return (
      <div className={styles.box}>
        <Link to={path} className={styles.layout}>
          {content}
        </Link>
      </div>
    );
  }
};
